import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Grid,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MAIN_COLOR_RED } from "../assets/styles";
import { SavedShoppingList } from "../redux/shoppingListSlice";
import { ShoppingPreferences } from "./SelectPreferencesStep";
import { useProducts } from "../hooks/useProducts";
import type { CalculateRouteResponse } from "../api/types";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RouteIcon from "@mui/icons-material/Route";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FilterListIcon from "@mui/icons-material/FilterList";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import StarIcon from "@mui/icons-material/Star";
import aiLogo from "../assets/img/ai-logo.png";
import PersonaRecommendationsDialog from "./PersonaRecommendationsDialog";

interface RouteSummaryDialogProps {
    open: boolean;
    selectedList: SavedShoppingList | null;
    preferences: ShoppingPreferences;
    personaId: string | null;
    routeResult?: CalculateRouteResponse | null;
    onStart: () => void;
    onClose?: () => void;
    /** Called when a recommended product is added so parent can update selectedList and Total items. */
    onRecommendationItemAdded?: (productId: string, quantity: number) => void;
}

const RouteSummaryDialog: React.FC<RouteSummaryDialogProps> = ({
    open,
    selectedList,
    preferences,
    personaId,
    routeResult,
    onStart,
    onClose,
    onRecommendationItemAdded,
}) => {
    const { getProductById } = useProducts();
    const [showRecommendationsPopup, setShowRecommendationsPopup] = useState(false);

    // Calculate total items
    const totalItems = selectedList
        ? Object.values(selectedList.items).reduce((sum, qty) => sum + qty, 0)
        : 0;

    // Calculate categories count from actual product data
    const categories = selectedList
        ? new Set(
              Object.keys(selectedList.items)
                  .map((productId) => {
                      const product = getProductById(productId);
                      return product?.category;
                  })
                  .filter((cat) => cat && cat.trim() !== "")
          )
        : new Set();
    const categoriesCount = categories.size;

    const hasValidRoute = !!(routeResult && routeResult.route && routeResult.route.length > 0);
    const estimatedTime =
        hasValidRoute && routeResult?.time != null ? `${routeResult.time} min` : "-- min";
    const distanceText =
        routeResult?.distance != null ? `${routeResult.distance} m` : null;

    // Get active constraints
    const activeConstraints = [];
    if (preferences.frozenItems) activeConstraints.push({ icon: <AcUnitIcon />, label: "Frozen items" });
    if (preferences.heavyItems) activeConstraints.push({ icon: <ShoppingBagIcon />, label: "Heavy items" });
    if (preferences.recommendations) activeConstraints.push({ icon: <StarIcon />, label: "Recommendations" });

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    p: 3,
                },
            }}
        >
            <DialogContent sx={{ p: 0, position: "relative" }}>
                {onClose && (
                    <IconButton
                        onClick={onClose}
                        aria-label="Close"
                        size="small"
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "text.secondary",
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    {/* AI Icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            border: `3px solid ${MAIN_COLOR_RED}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            mb: 2,
                        }}
                    >
                        <img
                            src={aiLogo}
                            alt="AI Logo"
                            style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: MAIN_COLOR_RED, mb: 3 }}
                    >
                        Your AI-Optimized Shopping Route:
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Estimated Time & Distance */}
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <AccessTimeIcon
                                    sx={{
                                        fontSize: 32,
                                        color: MAIN_COLOR_RED,
                                        mb: 1,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ color: MAIN_COLOR_RED, mb: 0.5 }}
                                >
                                    Estimated time:
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {estimatedTime}
                                </Typography>
                                {distanceText != null && (
                                    <>
                                        <RouteIcon
                                            sx={{
                                                fontSize: 32,
                                                color: MAIN_COLOR_RED,
                                                mt: 2,
                                                mb: 1,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            sx={{ color: MAIN_COLOR_RED, mb: 0.5 }}
                                        >
                                            Distance:
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {distanceText}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Grid>

                        {/* Total Items */}
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <ShoppingCartIcon
                                    sx={{
                                        fontSize: 32,
                                        color: MAIN_COLOR_RED,
                                        mb: 1,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ color: MAIN_COLOR_RED, mb: 0.5 }}
                                >
                                    Total items:
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {totalItems} items
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {categoriesCount} Categories
                                </Typography>
                                {/* Category Icons */}
                                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            bgcolor: "#e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="caption">🍞</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            bgcolor: "#e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="caption">🍎</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            bgcolor: "#e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="caption">🥛</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            bgcolor: "#e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <AcUnitIcon sx={{ fontSize: 16, color: "#666" }} />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Constraints */}
                        {activeConstraints.length > 0 && (
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <FilterListIcon
                                        sx={{
                                            fontSize: 32,
                                            color: MAIN_COLOR_RED,
                                            mb: 1,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ color: MAIN_COLOR_RED, mb: 1 }}
                                    >
                                        Constraints:
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                        {activeConstraints.map((constraint, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {constraint.icon}
                                            </Box>
                                        ))}
                                    </Box>
                                    {preferences.recommendations && personaId && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<StarIcon />}
                                            onClick={() => setShowRecommendationsPopup(true)}
                                            sx={{
                                                mt: 1.5,
                                                borderColor: MAIN_COLOR_RED,
                                                color: MAIN_COLOR_RED,
                                                "&:hover": {
                                                    borderColor: MAIN_COLOR_RED,
                                                    backgroundColor: "rgba(220, 38, 38, 0.04)",
                                                },
                                            }}
                                        >
                                            View recommended products
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {/* Start Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onStart}
                        disabled={!hasValidRoute}
                        sx={{
                            backgroundColor: hasValidRoute ? MAIN_COLOR_RED : "rgba(0,0,0,0.12)",
                            color: hasValidRoute ? "white" : "rgba(0,0,0,0.26)",
                            borderRadius: 2,
                            py: 1.5,
                            mt: 3,
                            fontWeight: "bold",
                            "&:hover": {
                                backgroundColor: hasValidRoute ? MAIN_COLOR_RED : "rgba(0,0,0,0.12)",
                            },
                        }}
                    >
                        Start
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>

        <PersonaRecommendationsDialog
            open={showRecommendationsPopup}
            onClose={() => setShowRecommendationsPopup(false)}
            personaId={personaId}
            selectedListId={selectedList?.id ?? null}
            onItemAdded={onRecommendationItemAdded ?? (() => {})}
        />
        </>
    );
};

export default RouteSummaryDialog;

